import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  Space,
  message,
  Row,
  Col,
  Select,
  Tag,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { OrgService } from "../../services";
import {
  Org,
  CreateOrgDto,
  UpdateOrgDto,
  OrgQueryParams,
  OrgStatus,
} from "../../types/org";
import ErrorHandler from "../../utils/errorHandler";
import MainLayout from "../../components/layout/MainLayout";
import dayjs from "dayjs";
import "./OrgManagement.scss";

const { Option } = Select;

const OrgManagementPage: React.FC = () => {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Org | null>(null);
  const [searchParams, setSearchParams] = useState<OrgQueryParams>({});
  const [pagination, setPagination] = useState({
    current: 1,
    limit: 10,
    total: 0,
  });
  const [orgForm] = Form.useForm();
  const [searchForm] = Form.useForm();

  // 加载组织列表
  const loadOrgs = async (params?: OrgQueryParams) => {
    setLoading(true);
    try {
      // 注意：翻页/切换 pageSize 时，Table 会把 page/pageSize 作为参数传进来
      // 这里必须优先使用 params 里的 page/limit，否则会被 pagination 的旧值覆盖，导致“点第二页仍请求第一页”
      const paramPage =
        typeof params?.page === "number" && Number.isFinite(params.page)
          ? params.page
          : undefined;
      const paramLimit =
        typeof params?.limit === "number" && Number.isFinite(params.limit)
          ? params.limit
          : undefined;

      const queryParams = {
        ...searchParams,
        ...params,
        page: paramPage ?? pagination.current,
        limit: paramLimit ?? pagination.limit,
      };

      const response = await OrgService.getOrgList(queryParams);

      if (response.code === 0 && response.data) {
        const data = response.data;
        setOrgs(data.data || []);
        setPagination((prev) => ({
          ...prev,
          current: data.page || 1,
          total: data.total || 0,
        }));
      } else {
        message.error(response.msg || "获取组织列表失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 使用独立参数加载组织，避免状态合并问题
  const loadOrgsWithParams = async (params: any) => {
    setLoading(true);
    try {
      const response = await OrgService.getOrgList(params);

      if (response.code === 0 && response.data) {
        const data = response.data;
        setOrgs(data.data || []);
        setPagination((prev) => ({
          ...prev,
          current: data.page || 1,
          total: data.total || 0,
        }));
      } else {
        message.error(response.msg || "获取组织列表失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 保存组织
  const handleSaveOrg = async (values: CreateOrgDto | UpdateOrgDto) => {
    try {
      setLoading(true);
      let response;

      if (editingOrg) {
        // 更新
        response = await OrgService.updateOrg(editingOrg.id, values);
      } else {
        // 创建
        response = await OrgService.createOrg(values as CreateOrgDto);
      }

      if (response.code === 0) {
        message.success(`${editingOrg ? "更新" : "创建"}组织成功`);
        handleCloseModal();
        loadOrgs();
      } else {
        message.error(response.msg || "操作失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 打开组织弹窗
  const handleOpenModal = (org?: Org) => {
    setEditingOrg(org || null);
    // 先重置表单，确保干净的状态
    orgForm.resetFields();
    setModalVisible(true);

    // 如果是编辑模式，在下一个事件循环中设置值，确保重置完成
    if (org) {
      setTimeout(() => {
        orgForm.setFieldsValue(org);
      }, 0);
    }
  };

  // 关闭组织弹窗
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingOrg(null);
    // 关闭时也重置表单
    orgForm.resetFields();
  };

  // 搜索组织
  const handleSearch = async (values: OrgQueryParams) => {
    const params = {
      ...values,
      page: 1, // 搜索时重置到第一页
    };
    setSearchParams(params);
    setPagination((prev) => ({ ...prev, current: 1 }));
    await loadOrgs(params);
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    const resetParams = {};
    setSearchParams(resetParams);
    setPagination((prev) => ({ ...prev, current: 1 }));
    // 直接传入重置后的参数，包括页码重置为1
    loadOrgsWithParams({ page: 1, limit: pagination.limit });
  };

  // 渲染组织状态
  const renderStatus = (status: OrgStatus) => {
    const statusMap: Record<OrgStatus, { text: string; color: string }> = {
      active: { text: "活跃", color: "green" },
      inactive: { text: "非活跃", color: "default" },
      suspended: { text: "已暂停", color: "red" },
    };

    const config = statusMap[status] || { text: status, color: "default" };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 组织表格列
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
    },
    {
      title: "组织名称",
      dataIndex: "name",
      key: "name",
      width: 150,
    },
    {
      title: "组织标识符",
      dataIndex: "identifier",
      key: "identifier",
      width: 150,
    },
    {
      title: "组织描述",
      dataIndex: "description",
      key: "description",
      width: 200,
      ellipsis: true,
      render: (text: string) => text || "-",
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: renderStatus,
    },
    {
      title: "创建时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (time: string) =>
        time ? dayjs(time).format("YYYY-MM-DD HH:mm:ss") : "-",
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      render: (time: string) =>
        time ? dayjs(time).format("YYYY-MM-DD HH:mm:ss") : "-",
    },
    {
      title: "操作",
      key: "action",
      width: 200,
      render: (text: any, record: Org) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout title="组织管理">
      <div className="org-management">
        {/* 搜索区域 */}
        <Card className="search-card" style={{ marginBottom: 16 }}>
          <Form form={searchForm} layout="inline" onFinish={handleSearch}>
            <Form.Item name="name" label="组织名称">
              <Input
                placeholder="请输入组织名称"
                style={{ width: 200 }}
                allowClear
              />
            </Form.Item>
            <Form.Item name="identifier" label="组织标识符">
              <Input
                placeholder="请输入组织标识符"
                style={{ width: 200 }}
                allowClear
              />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select
                placeholder="请选择状态"
                style={{ width: 150 }}
                allowClear
              >
                <Option value="active">活跃</Option>
                <Option value="inactive">非活跃</Option>
                <Option value="suspended">已暂停</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  搜索
                </Button>
                <Button onClick={handleReset} icon={<ReloadOutlined />}>
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* 组织列表 */}
        <Card
          title="组织列表"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
            >
              新增组织
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={orgs}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.limit,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: (page, pageSize) => {
                const nextLimit = pageSize || 10;
                const isSizeChange = nextLimit !== pagination.limit;
                const nextPage = isSizeChange ? 1 : page;

                setPagination((prev) => ({
                  ...prev,
                  current: nextPage,
                  limit: nextLimit,
                }));

                // 只请求一次列表接口；并且保证请求参数与 UI 一致
                loadOrgs({ page: nextPage, limit: nextLimit });
              },
            }}
          />
        </Card>

        {/* 组织弹窗 */}
        <Modal
          title={`${editingOrg ? "编辑" : "新增"}组织`}
          open={modalVisible}
          onOk={() => orgForm.submit()}
          onCancel={handleCloseModal}
          confirmLoading={loading}
          width={600}
        >
          <Form form={orgForm} layout="vertical" onFinish={handleSaveOrg}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="组织名称"
                  rules={[
                    { required: true, message: "组织名称不能为空" },
                    { max: 100, message: "组织名称不能超过100个字符" },
                  ]}
                >
                  <Input placeholder="请输入组织名称" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="identifier"
                  label="组织标识符"
                  rules={[
                    { required: !editingOrg, message: "组织标识符不能为空" },
                    {
                      max: 50,
                      message: "组织标识符不能超过50个字符",
                    },
                    {
                      pattern: /^[a-zA-Z0-9_]+$/,
                      message: "组织标识符仅支持字母、数字、下划线",
                    },
                  ]}
                >
                  <Input
                    placeholder="例如：test_org_001"
                    disabled={!!editingOrg}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="status" label="状态">
                  <Select placeholder="请选择状态" defaultValue="active">
                    <Option value="active">活跃</Option>
                    <Option value="inactive">非活跃</Option>
                    <Option value="suspended">已暂停</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="description" label="组织描述">
                  <Input.TextArea
                    placeholder="请输入组织描述（可选）"
                    rows={3}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default OrgManagementPage;
